import { Link } from "@inertiajs/react"
import { type ReactNode } from "react"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"

interface RegionStat {
  id: number
  name: string
  pois_count: number
  properties_count: number
  sessions_30d: number
}

interface Props {
  distributor: {
    id: number
    name: string
    email: string
    last_activity: string | null
  }
  regions: RegionStat[]
  urls: { index: string }
}

export default function DistributorShow({ distributor, regions, urls }: Props) {
  const totalPois       = regions.reduce((n, r) => n + r.pois_count, 0)
  const totalProperties = regions.reduce((n, r) => n + r.properties_count, 0)
  const totalSessions   = regions.reduce((n, r) => n + r.sessions_30d, 0)

  return (
    <div>
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
          <Link href={urls.index} className="hover:underline">Distributors</Link>
          <span aria-hidden="true">/</span>
          <span>{distributor.name}</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{distributor.name}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">{distributor.email}</p>
        {distributor.last_activity && (
          <p className="mt-1 text-xs text-muted-foreground">
            Last content activity: {new Date(distributor.last_activity).toLocaleDateString()}
          </p>
        )}
      </div>

      <div className="mb-6 grid grid-cols-3 gap-4">
        <div className="rounded-xl border bg-card px-4 py-3">
          <p className="text-xs text-muted-foreground">Total POIs</p>
          <p className="mt-1 text-2xl font-semibold">{totalPois}</p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3">
          <p className="text-xs text-muted-foreground">Properties</p>
          <p className="mt-1 text-2xl font-semibold">{totalProperties}</p>
        </div>
        <div className="rounded-xl border bg-card px-4 py-3">
          <p className="text-xs text-muted-foreground">Guest sessions (30d)</p>
          <p className="mt-1 text-2xl font-semibold">{totalSessions}</p>
        </div>
      </div>

      {regions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No regions assigned.</p>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">POIs</TableHead>
                <TableHead className="text-right">Properties</TableHead>
                <TableHead className="text-right">Sessions (30d)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {regions.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{r.pois_count}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{r.properties_count}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{r.sessions_30d}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

DistributorShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
